import {
    type MoneyMapAccountWithAccountDetails,
    MoneyMapCategory,
    MoneyMapRuleGoalType,
    MoneyMapRuleType,
    PaycheckRoutingRuleType,
} from '@fruitful/types';
import { useAtom, useSetAtom } from 'jotai';
import React, { useEffect, useMemo, useState } from 'react';
import { Keyboard } from 'react-native';
import {
    BodyLargeEmphasized,
    BodyLargeRegular,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    DollarInput,
    H6Emphasized,
    Icon,
    InfoIcon,
    Input,
    KeyboardCompatibleBottomSheetTextInput,
    LabelRegular,
    LockIcon,
    PercentInput,
    Sheet,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { MONEY_MAP_NAME_MAX_LENGTH } from '../constants';
import {
    deleteRuleInMoneyMap,
    editableMoneyMapAtom,
    selectedMoneyMapRuleAtom,
    updateRuleInMoneyMap,
} from '../state/editableMoneyMapAtom';
import {
    formatCentsForDollarInput,
    parseDollarInputToCents,
    parsePercentInputToDecimal,
    percentToInputString,
} from '../utils/moneyMapFormInputUtils';
import { isTargetBalanceEligibleAccountType } from '../utils/moneyMapRuleUtils';
import { MoneyMapAccountIcon } from './MoneyMapAccountIcon';

const RULE_TYPE_TO_CATEGORY: Record<MoneyMapRuleType, MoneyMapCategory> = {
    [MoneyMapRuleType.BILLS]: MoneyMapCategory.BILLS,
    [MoneyMapRuleType.SPEND]: MoneyMapCategory.SPEND,
    [MoneyMapRuleType.GOAL]: MoneyMapCategory.GOALS,
    [MoneyMapRuleType.ACCOUNT_ONLY]: MoneyMapCategory.OTHER_ACCOUNTS,
};

type ContributionLimitType = 'Monthly' | 'Annual';

interface ContributionLimitInfoSheetProps {
    isOpen: boolean;
    onClose: () => void;
    contributionMaxType: ContributionLimitType | null;
}

const ContributionLimitInfoSheet = ({
    isOpen,
    onClose,
    contributionMaxType,
}: ContributionLimitInfoSheetProps) => {
    if (contributionMaxType == null) return null;

    const period = contributionMaxType === 'Monthly' ? 'month' : 'year';

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            showCloseButton={false}
            isInsideModal
        >
            <YStack gap={14} paddingTop={20} maxWidth={500}>
                <H6Emphasized>{contributionMaxType} limit</H6Emphasized>
                <BodyMediumRegular color="$gray900">
                    We&apos;ll track a running total of any contributions made
                    from prior Income Splits between the first and last day of
                    the calendar {period}. Contributions and limits are
                    calculated for each rule individually.
                </BodyMediumRegular>
                <BodyMediumRegular color="$gray900">
                    Once you&apos;ve reached the{' '}
                    {contributionMaxType.toLowerCase()} limit, no further
                    contributions will be made until the following {period}.
                </BodyMediumRegular>
                <Button
                    marginTop={16}
                    text="Dismiss"
                    variant={ButtonVariant.PRIMARY}
                    onPress={onClose}
                />
            </YStack>
        </Sheet>
    );
};

const TargetBalanceInfoSheet = ({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}) => {
    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            showCloseButton={false}
            isInsideModal
        >
            <YStack gap={14} paddingTop={20} maxWidth={500}>
                <H6Emphasized>Target balance</H6Emphasized>
                <BodyMediumRegular color="$gray900">
                    We&apos;ll send money to this account to build up to, or
                    replenish, the target balance.
                </BodyMediumRegular>
                <Button
                    marginTop={16}
                    text="Dismiss"
                    variant={ButtonVariant.PRIMARY}
                    onPress={onClose}
                />
            </YStack>
        </Sheet>
    );
};

const OptionalLimitLabel = ({
    label,
    onPressInfo,
}: {
    label: string;
    onPressInfo: () => void;
}) => {
    return (
        <XStack gap={8} alignItems="center">
            <LabelRegular marginLeft={8} color="$gray1100">
                {label}
            </LabelRegular>
            <View onPress={onPressInfo} cursor="pointer" hitSlop={8}>
                <Icon size={12} color="$gray1100" icon={InfoIcon} />
            </View>
        </XStack>
    );
};

interface DeleteConfirmSheetProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

const DeleteConfirmSheet = ({
    isOpen,
    onClose,
    onConfirm,
}: DeleteConfirmSheetProps) => {
    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="Delete rule"
            showCloseButton={false}
        >
            <YStack gap={24}>
                <YStack gap={16}>
                    <BodyLargeEmphasized>
                        Are you sure you want to delete this rule?
                    </BodyLargeEmphasized>
                    <BodyMediumRegular color="$gray1000">
                        Deleting this rule will also reset the calculation for
                        your monthly and annual contribution limits.
                    </BodyMediumRegular>
                </YStack>

                <YStack gap={12}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        tone="danger"
                        text="Yes, delete this rule"
                        onPress={onConfirm}
                    />
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        text="Cancel"
                        onPress={onClose}
                    />
                </YStack>
            </YStack>
        </Sheet>
    );
};

interface MoneyMapRuleEditSheetProps {
    isOpen: boolean;
    onClose: () => void;
    accounts: MoneyMapAccountWithAccountDetails[];
}

export const MoneyMapRuleEditSheet = ({
    isOpen,
    onClose,
    accounts,
}: MoneyMapRuleEditSheetProps) => {
    const [selectedRule, setSelectedRule] = useAtom(selectedMoneyMapRuleAtom);
    const setEditableMoneyMap = useSetAtom(editableMoneyMapAtom);

    const [goalName, setGoalName] = useState('');
    const [amount, setAmount] = useState('');
    const [percentage, setPercentage] = useState('');
    const [targetBalance, setTargetBalance] = useState('');
    const [monthlyCap, setMonthlyCap] = useState('');
    const [yearlyCap, setYearlyCap] = useState('');
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [limitInfoType, setLimitInfoType] =
        useState<ContributionLimitType | null>(null);
    const [isTargetBalanceInfoOpen, setIsTargetBalanceInfoOpen] =
        useState(false);

    useEffect(() => {
        if (selectedRule && isOpen) {
            setGoalName(selectedRule.description);
            // BILLS amount lives in targetBalanceCents; SPEND in monthlyContributionMaxCents.
            const amountCents =
                selectedRule.routingRuleType ===
                PaycheckRoutingRuleType.MINIMUM_BALANCE
                    ? selectedRule.targetBalanceCents
                    : selectedRule.monthlyContributionMaxCents;
            setAmount(formatCentsForDollarInput(amountCents));
            setPercentage(percentToInputString(selectedRule.percentage));
            setTargetBalance(
                formatCentsForDollarInput(selectedRule.targetBalanceCents),
            );
            setMonthlyCap(
                formatCentsForDollarInput(
                    selectedRule.monthlyContributionMaxCents,
                ),
            );
            setYearlyCap(
                formatCentsForDollarInput(
                    selectedRule.yearlyContributionMaxCents,
                ),
            );
        }
    }, [selectedRule, isOpen]);

    const handleClose = () => {
        setSelectedRule(null);
        setLimitInfoType(null);
        setIsTargetBalanceInfoOpen(false);
        onClose();
    };

    const handleSave = () => {
        if (!selectedRule) return;

        setEditableMoneyMap((prev) => {
            if (!prev) return prev;

            const patch: Parameters<typeof updateRuleInMoneyMap>[2] = {};

            if (isGoalRule) {
                patch.description = goalName.trim();
            }
            if (showAmount) {
                const cents = parseDollarInputToCents(amount);
                if (
                    selectedRule.routingRuleType ===
                    PaycheckRoutingRuleType.MINIMUM_BALANCE
                ) {
                    patch.targetBalanceCents = cents;
                } else {
                    patch.monthlyContributionMaxCents = cents;
                }
            }
            if (showPercentage) {
                patch.percentage = parsePercentInputToDecimal(percentage);
            }
            if (showTargetBalance) {
                const targetBalanceCents =
                    parseDollarInputToCents(targetBalance);
                patch.targetBalanceCents = targetBalanceCents;
                // Keep goalType/routingRuleType in step with the target
                // balance — mirrors MoneyMapAddGoalSheet's create logic, so
                // editing an existing goal's target balance can't leave it
                // stuck as CONTRIBUTE_INDEFINITELY while a balance is set
                // (or vice versa). A DEBT_PAY_DOWN goal keeps that label as
                // long as it still has a target balance.
                if (targetBalanceCents != null) {
                    patch.goalType =
                        selectedRule.goalType ===
                        MoneyMapRuleGoalType.DEBT_PAY_DOWN
                            ? MoneyMapRuleGoalType.DEBT_PAY_DOWN
                            : MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE;
                    patch.routingRuleType =
                        PaycheckRoutingRuleType.MINIMUM_BALANCE;
                } else {
                    patch.goalType =
                        MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY;
                    patch.routingRuleType = PaycheckRoutingRuleType.PERCENTAGE;
                }
            }
            if (showMonthlyCap) {
                patch.monthlyContributionMaxCents =
                    parseDollarInputToCents(monthlyCap);
            }
            if (showYearlyCap) {
                patch.yearlyContributionMaxCents =
                    parseDollarInputToCents(yearlyCap);
            }

            return updateRuleInMoneyMap(prev, selectedRule.id, patch);
        });
        handleClose();
    };

    const handleDelete = () => {
        if (!selectedRule) return;
        setEditableMoneyMap((prev) => {
            if (!prev) return prev;
            return deleteRuleInMoneyMap(prev, selectedRule.id);
        });
        setIsDeleteConfirmOpen(false);
        handleClose();
    };

    const resolvedAccount = useMemo(() => {
        if (!selectedRule) return null;
        return (
            accounts.find((a) => a.id === selectedRule.moneyMapAccountId) ??
            null
        );
    }, [selectedRule, accounts]);

    const accountName =
        resolvedAccount?.accountDetails?.name ??
        selectedRule?.description ??
        'Rule';

    const ruleType = selectedRule?.type;
    const isGoalRule =
        ruleType === MoneyMapRuleType.GOAL ||
        ruleType === MoneyMapRuleType.ACCOUNT_ONLY;
    // Product copy is "monthly" for both; BILLS is still persisted as a target balance.
    const amountLabel =
        ruleType === MoneyMapRuleType.BILLS
            ? 'Monthly bills amount'
            : ruleType === MoneyMapRuleType.SPEND
              ? 'Monthly spend amount'
              : 'Monthly amount';

    const fieldVisibility = useMemo(() => {
        if (!selectedRule) {
            return {
                showAmount: false,
                showPercentage: true,
                showTargetBalance: true,
                showMonthlyCap: true,
                showYearlyCap: true,
            };
        }

        const ruleType = selectedRule.type;
        const accountType = selectedRule.moneyMapAccount.type;
        const targetBalanceDisabled =
            !isTargetBalanceEligibleAccountType(accountType);

        if (
            ruleType === MoneyMapRuleType.BILLS ||
            ruleType === MoneyMapRuleType.SPEND
        ) {
            return {
                showAmount: true,
                showPercentage: false,
                showTargetBalance: false,
                showMonthlyCap: false,
                showYearlyCap: false,
            };
        }

        return {
            showAmount: false,
            showPercentage: true,
            showTargetBalance: !targetBalanceDisabled,
            showMonthlyCap: true,
            showYearlyCap: true,
        };
    }, [selectedRule]);

    const {
        showAmount,
        showPercentage,
        showTargetBalance,
        showMonthlyCap,
        showYearlyCap,
    } = fieldVisibility;

    const canSave = useMemo(() => {
        let isValid = true;

        // Required if shown
        if (isGoalRule && goalName.trim().length === 0) {
            isValid = false;
        }
        if (isValid && showAmount) {
            const value = parseDollarInputToCents(amount);
            isValid = value !== null && value > 0;
        }
        if (isValid && showPercentage) {
            const value = parsePercentInputToDecimal(percentage);
            isValid = value != null && value > 0 && value <= 1;
        }

        // Target balance can always be cleared — handleSave converts the
        // goal to CONTRIBUTE_INDEFINITELY when it is. If filled, it must be
        // positive.
        if (isValid && showTargetBalance) {
            const value = parseDollarInputToCents(targetBalance);
            isValid = value != null ? value > 0 : true;
        }
        if (isValid && showMonthlyCap) {
            const value = parseDollarInputToCents(monthlyCap);
            isValid = value != null ? value > 0 : true;
        }
        if (isValid && showYearlyCap) {
            const value = parseDollarInputToCents(yearlyCap);
            isValid = value != null ? value > 0 : true;
        }
        return isValid;
    }, [
        isGoalRule,
        goalName,
        showAmount,
        amount,
        showPercentage,
        percentage,
        showTargetBalance,
        targetBalance,
        showMonthlyCap,
        monthlyCap,
        showYearlyCap,
        yearlyCap,
    ]);

    return (
        <>
            <Sheet
                isOpen={isOpen}
                onClose={handleClose}
                showCloseButton={false}
                // Goal and Account-only rules render far more fields than
                // Bills/Spend, and on smaller screens the keyboard can cover
                // Cancel/Save with no way to scroll to reveal them. This lets
                // the ScrollView reserve real scroll room for the keyboard —
                // a fallback for when the sheet's own keyboard-driven growth
                // isn't (or hasn't yet) made enough room on its own — without
                // forcing the sheet to always render at max height.
                {...(isGoalRule ? { keyboardScrollFallback: true } : {})}
            >
                <YStack $sm={{ minWidth: 400 }} gap={24}>
                    <XStack
                        width="100%"
                        alignItems="center"
                        justifyContent="center"
                        position="relative"
                    >
                        <BodyLargeRegular textAlign="center">
                            Edit rule
                        </BodyLargeRegular>
                        {isGoalRule && (
                            <View
                                position="absolute"
                                right={0}
                                onPress={() => {
                                    setIsDeleteConfirmOpen(true);
                                }}
                                cursor="pointer"
                                animation="button"
                                pressStyle={{ scale: 0.95 }}
                            >
                                <BodyMediumRegular color="$red900">
                                    Delete
                                </BodyMediumRegular>
                            </View>
                        )}
                    </XStack>

                    {isGoalRule && (
                        <YStack gap={8}>
                            <LabelRegular marginLeft={8} color="$gray1100">
                                Goal name
                            </LabelRegular>
                            <KeyboardCompatibleBottomSheetTextInput
                                component={Input}
                                value={goalName}
                                onChangeText={setGoalName}
                                maxLength={MONEY_MAP_NAME_MAX_LENGTH}
                                placeholder="Goal name"
                                returnKeyType="done"
                                onSubmitEditing={() => {
                                    Keyboard.dismiss();
                                }}
                                fontWeight="regular"
                            />
                        </YStack>
                    )}

                    <YStack gap={8}>
                        <LabelRegular marginLeft={8} color="$gray1100">
                            Destination account
                        </LabelRegular>
                        {/* Custom row: Input leftIcon can't host MoneyMapAccountIcon */}
                        <XStack
                            backgroundColor="$gray200"
                            borderWidth={1}
                            borderColor="$gray600"
                            borderRadius="$4"
                            height={56}
                            paddingHorizontal={16}
                            gap={8}
                            alignItems="center"
                        >
                            {selectedRule != null && (
                                <MoneyMapAccountIcon
                                    category={
                                        RULE_TYPE_TO_CATEGORY[selectedRule.type]
                                    }
                                    account={
                                        resolvedAccount ??
                                        selectedRule.moneyMapAccount
                                    }
                                />
                            )}
                            <BodyMediumRegular flex={1}>
                                {accountName}
                            </BodyMediumRegular>
                            <Icon icon={LockIcon} size={16} color="$gray1100" />
                        </XStack>
                    </YStack>

                    {showAmount && (
                        <YStack gap={8}>
                            <LabelRegular marginLeft={8} color="$gray1100">
                                {amountLabel}
                            </LabelRegular>
                            <KeyboardCompatibleBottomSheetTextInput
                                component={DollarInput}
                                value={amount}
                                onChangeText={setAmount}
                                allowDecimals={false}
                                returnKeyType="done"
                                onSubmitEditing={() => {
                                    Keyboard.dismiss();
                                }}
                                fontWeight="regular"
                            />
                        </YStack>
                    )}

                    {showPercentage && (
                        <YStack gap={8}>
                            <LabelRegular marginLeft={8} color="$gray1100">
                                Percentage
                            </LabelRegular>
                            <KeyboardCompatibleBottomSheetTextInput
                                component={PercentInput}
                                value={percentage}
                                onChangeText={setPercentage}
                                returnKeyType="done"
                                onSubmitEditing={() => {
                                    Keyboard.dismiss();
                                }}
                                fontWeight="regular"
                            />
                        </YStack>
                    )}

                    {showTargetBalance && (
                        <YStack gap={8}>
                            <OptionalLimitLabel
                                label="Target balance (optional)"
                                onPressInfo={() => {
                                    setIsTargetBalanceInfoOpen(true);
                                }}
                            />
                            <KeyboardCompatibleBottomSheetTextInput
                                component={DollarInput}
                                value={targetBalance}
                                onChangeText={setTargetBalance}
                                allowDecimals={false}
                                returnKeyType="done"
                                onSubmitEditing={() => {
                                    Keyboard.dismiss();
                                }}
                                fontWeight="regular"
                            />
                        </YStack>
                    )}

                    {(showMonthlyCap || showYearlyCap) && (
                        <XStack gap={12}>
                            {showMonthlyCap && (
                                <YStack gap={8} flex={1}>
                                    <OptionalLimitLabel
                                        label="Monthly limit (optional)"
                                        onPressInfo={() => {
                                            setLimitInfoType('Monthly');
                                        }}
                                    />
                                    <KeyboardCompatibleBottomSheetTextInput
                                        component={DollarInput}
                                        value={monthlyCap}
                                        onChangeText={setMonthlyCap}
                                        allowDecimals={false}
                                        returnKeyType="done"
                                        onSubmitEditing={() => {
                                            Keyboard.dismiss();
                                        }}
                                        fontWeight="regular"
                                    />
                                </YStack>
                            )}
                            {showYearlyCap && (
                                <YStack gap={8} flex={1}>
                                    <OptionalLimitLabel
                                        label="Annual limit (optional)"
                                        onPressInfo={() => {
                                            setLimitInfoType('Annual');
                                        }}
                                    />
                                    <KeyboardCompatibleBottomSheetTextInput
                                        component={DollarInput}
                                        value={yearlyCap}
                                        onChangeText={setYearlyCap}
                                        allowDecimals={false}
                                        returnKeyType="done"
                                        onSubmitEditing={() => {
                                            Keyboard.dismiss();
                                        }}
                                        fontWeight="regular"
                                    />
                                </YStack>
                            )}
                        </XStack>
                    )}

                    <XStack gap={12}>
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.LARGE}
                            text="Cancel"
                            onPress={handleClose}
                            buttonProps={{ flexShrink: 1 }}
                        />
                        <Button
                            disabled={!canSave}
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            text="Save"
                            onPress={handleSave}
                            buttonProps={{ flexShrink: 1 }}
                        />
                    </XStack>
                </YStack>
            </Sheet>

            <DeleteConfirmSheet
                isOpen={isDeleteConfirmOpen}
                onClose={() => {
                    setIsDeleteConfirmOpen(false);
                }}
                onConfirm={handleDelete}
            />
            <ContributionLimitInfoSheet
                isOpen={limitInfoType != null}
                onClose={() => {
                    setLimitInfoType(null);
                }}
                contributionMaxType={limitInfoType}
            />
            <TargetBalanceInfoSheet
                isOpen={isTargetBalanceInfoOpen}
                onClose={() => {
                    setIsTargetBalanceInfoOpen(false);
                }}
            />
        </>
    );
};
